import type { KeyboardEvent, MouseEvent } from 'react';
import { Tooltip } from '@elfsight/widget-modal';
import {
  getColorWithAlpha,
  getHandleActionKeyDown
} from '@elfsight/widget-utils';
import { generateFreeLinkRemoveURL, generateFreeLinkURL } from './links';
import {
  buttonModeLinkIconStyles,
  buttonModeLinkStyles,
  commonLinkIconStyles,
  commonLinkStyles,
  removeButtonIconStyles,
  removeButtonStyles,
  removeTextForceTwoLinesStyles,
  removeTextStyles,
  setStyleAttribute,
  textModeLinkIconStyles,
  textModeLinkStyles
} from './styles';
import type { FreeLinkConfig } from './types';

const DEFAULT_TEXT_MODE_PROPS = {
  color: 'rgb(17, 17, 17)',
  customColor: undefined,
  fontSize: 11,
  forceTwoLines: false
};

export function FreeLink({
  appAlias,
  appName,
  displayFreeLink = false,
  displayFreeLinkRemove = true,
  freeLinkAnchor,
  freeLinkURL,
  freeLinkRemoveURL,
  iconHidden = false,
  mode = 'button',
  textModeProps = DEFAULT_TEXT_MODE_PROPS,
  style
}: FreeLinkConfig) {
  const anchorMapping = {
    button: `Free ${appName} Widget`,
    text: `Free ${appName} Widget by Elfsight`
  };
  const anchor = freeLinkAnchor || anchorMapping[mode];
  const url = generateFreeLinkURL(freeLinkURL, appAlias);
  const removeURL = generateFreeLinkRemoveURL(freeLinkRemoveURL, appAlias);

  const displayRemoveButton = displayFreeLinkRemove && mode === 'button';
  const displayRemoveText = displayFreeLinkRemove && mode === 'text';

  const currentLinkIconStyles =
    mode === 'button' ? buttonModeLinkIconStyles : textModeLinkIconStyles;

  const textMode = {
    ...DEFAULT_TEXT_MODE_PROPS,
    ...textModeProps
  };

  const currentLinkStyles =
    mode === 'button'
      ? buttonModeLinkStyles
      : {
          ...textModeLinkStyles,
          ...(textMode.forceTwoLines ? { height: 'fit-content' } : {}),
          fontSize: `${textMode.fontSize}px`,
          color: textMode.customColor || getColorWithAlpha(textMode.color!, 0.5)
        };

  const handleFreeLinkRemove = (
    e: MouseEvent<Element> | KeyboardEvent<Element>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(removeURL, '_blank');
  };

  if (!displayFreeLink) {
    return null;
  }

  return (
    <Tooltip
      title={
        <span
          style={{
            fontSize: '12px !important',
            fontWeight: '400 !important',
            lineHeight: '14px !important'
          }}
        >
          Powered by Elfsight
        </span>
      }
      borderRadius={8}
      disabled={mode === 'text'}
    >
      <a
        href={url.toString()}
        target="_blank"
        rel="noreferrer"
        ref={(el) =>
          setStyleAttribute(el, {
            ...commonLinkStyles,
            ...currentLinkStyles,
            ...style?.link
          })
        }
      >
        {!iconHidden && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            ref={(el) =>
              setStyleAttribute(el, {
                ...commonLinkIconStyles,
                ...currentLinkIconStyles,
                ...style?.linkIcon
              })
            }
          >
            <path d="M8.28439 0.619913C12.3385 0.762025 15.5217 4.18821 15.3801 8.25775C15.2385 12.3271 11.825 15.5221 7.77092 15.38C3.71669 15.2379 0.533499 11.8117 0.67508 7.74231C0.681887 7.54618 0.691595 7.35236 0.714312 7.16072C0.725872 7.06324 0.877483 6.32426 1.53911 6.41239C2.03511 6.4785 2.19208 6.91401 2.15542 7.24594C2.14837 7.30974 2.13399 7.4785 2.13399 7.4785C2.12489 7.58249 2.11845 7.68724 2.11474 7.79276C2.00087 11.0653 4.56082 13.8207 7.82114 13.935C11.0814 14.0493 13.8265 11.4798 13.9404 8.20729C14.0543 4.93459 11.4943 2.17923 8.23413 2.06493C7.92633 2.05415 7.62308 2.06746 7.32627 2.10294L7.14896 2.12687C7.14896 2.12687 6.29609 2.26862 6.10609 1.69051C5.92226 1.13111 6.35172 0.807509 6.64245 0.746823C7.17277 0.644899 7.72238 0.600183 8.28439 0.619913ZM0.377094 1.37956C0.445891 1.37956 0.510217 1.39852 0.565762 1.43111L8.32629 5.42412C8.34941 5.43552 8.37216 5.44736 8.3948 5.45937L8.42404 5.47441L8.42368 5.47482L8.50269 5.51975C9.15218 5.90418 9.57631 6.53595 9.57631 7.25063C9.57631 7.25597 9.57587 7.26119 9.57583 7.26652L9.57631 7.26596L9.5764 9.94954C9.57641 9.97238 9.57642 9.99453 9.57643 10.016L9.57676 10.4381C9.57677 10.4482 9.57678 10.4577 9.5768 10.4666L9.57696 10.567C9.57704 10.5702 9.5774 10.5733 9.5774 10.5765C9.5774 10.7875 9.40855 10.9584 9.20018 10.9584C9.13614 10.9584 9.07588 10.9421 9.02304 10.9136L7.2201 10.0035L7.22247 10.0007C6.49144 9.66056 5.99601 9.01497 5.98896 8.27267C5.98848 7.71587 5.90067 7.06934 5.0132 6.30206C4.46012 5.824 3.79937 5.24999 3.14497 4.68033L2.93866 4.50069C1.60076 3.33554 0.340225 2.23316 0.143314 2.06093L0.121764 2.04209C0.118421 2.03914 0.116165 2.0372 0.116165 2.0372L0.0873202 2.00556C0.0328777 1.93938 0 1.85438 0 1.7615C0 1.5505 0.168851 1.37956 0.377094 1.37956Z" />
          </svg>
        )}

        {anchor}

        {displayRemoveButton && (
          <div
            tabIndex={0}
            title="Remove Elfsight branding"
            role="button"
            ref={(el) =>
              setStyleAttribute(el, {
                ...removeButtonStyles,
                ...style?.remove
              })
            }
            onClick={handleFreeLinkRemove}
            onKeyDown={getHandleActionKeyDown(handleFreeLinkRemove)}
            onMouseOver={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 12 12"
              ref={(el) =>
                setStyleAttribute(el, {
                  ...removeButtonIconStyles,
                  ...style?.removeIcon
                })
              }
            >
              <path d="M2.41578 1.71578L2.46289 1.75737L5.99937 5.29255L9.53586 1.75737C9.73115 1.56213 10.0477 1.56216 10.243 1.75745C10.4232 1.93771 10.437 2.22133 10.2845 2.41746L10.2429 2.46456L6.70687 6.00005L10.2429 9.53554C10.4382 9.73078 10.4382 10.0474 10.243 10.2426C10.0627 10.4229 9.77913 10.4368 9.58297 10.2843L9.53586 10.2427L5.99937 6.70705L2.46289 10.2427C2.2676 10.438 1.95102 10.4379 1.75578 10.2426C1.57556 10.0624 1.56173 9.77877 1.71426 9.58264L1.75586 9.53554L5.29137 6.00005L1.75586 2.46456C1.56058 2.26932 1.56054 1.95274 1.75578 1.75745C1.936 1.57719 2.21961 1.56329 2.41578 1.71578Z" />
            </svg>
          </div>
        )}

        {displayRemoveText && (
          <div
            tabIndex={0}
            role="link"
            ref={(el) => {
              setStyleAttribute(el, {
                ...removeTextStyles,
                ...(textMode.forceTwoLines
                  ? removeTextForceTwoLinesStyles
                  : {}),
                ...style?.removeText
              });

              if (el) {
                el.onmouseenter = () => {
                  el.style.color = textMode.customColor
                    ? getColorWithAlpha(textMode.customColor, 1)
                    : textMode.color;
                };
                el.onmouseleave = () => {
                  el.style.color = '';
                };
              }
            }}
            onClick={handleFreeLinkRemove}
            onKeyDown={getHandleActionKeyDown(handleFreeLinkRemove)}
          >
            Remove Branding
          </div>
        )}
      </a>
    </Tooltip>
  );
}
